<script lang="ts" setup>
import { ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { VForm } from "vuetify/components";

import OmniDialog from "@/components/omni/OmniDialog.vue";
import OrderExpenseForm from "@/views/orders/view/components/OrderExpenseForm.vue";
import { useOrderExpenses } from "@/composables";

import type { CreateOrderExpense, OrderExpense } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { isLoading, updateOrderExpense, createOrderExpenseDraft } = useOrderExpenses();

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
  orderId: string;
  expense: OrderExpense | null;
}>();

const emit = defineEmits<{
  (e: "success", expense: OrderExpense): void;
}>();

const formRef = useTemplateRef<VForm>("form");
const editedExpense = ref<CreateOrderExpense>(createOrderExpenseDraft());

function close() {
  showDialog.value = false;
}

async function saveExpense() {
  const validation = await formRef.value?.validate();

  if (!validation?.valid || !props.expense) {
    return;
  }

  const updatedExpense = await updateOrderExpense(props.orderId, props.expense.id, editedExpense.value);

  if (!updatedExpense) {
    return;
  }

  emit("success", updatedExpense);
  close();
}

watch(showDialog, (value) => {
  if (!value || !props.expense) {
    return;
  }

  editedExpense.value = {
    type: props.expense.type,
    rate: props.expense.rate,
    note: props.expense.note ?? null,
    affectsDispatcherProfit: props.expense.affectsDispatcherProfit,
  };
});
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :primary-action-label="t('general.save')"
    :primary-button-loading="isLoading"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isLoading"
    :title="t('payments.edit-expense')"
    width="800"
    @primary-action:click="saveExpense"
    @secondary-action:click="close"
  >
    <v-form ref="form">
      <order-expense-form v-model="editedExpense" />
    </v-form>
  </omni-dialog>
</template>
